<template>
  <v-card
    class="library-card"
    :class="{
      'library-card--featured': featured,
      'library-card--compact': compact,
    }"
    color="surface"
    variant="outlined"
    :to="`/reader/${article.id}`"
    hover
  >
    <v-img :src="articleImage(article)" class="library-card__image" cover />

    <div class="library-card__body">
      <div class="library-card__meta">
        <span v-if="article.type">{{ article.type.toUpperCase() }}</span>
        <span v-if="articleDate(article)">{{ articleDate(article) }}</span>
        <span v-if="article.views">{{ formatViews(article.views) }}</span>
      </div>

      <h2>{{ article.title || "Untitled article" }}</h2>

      <p class="library-card__excerpt">
        {{ articleExcerpt(article) }}
      </p>

      <div class="library-card__footer">
        <span>{{ articleSource(article) }}</span>
        <v-icon size="20">mdi-arrow-right</v-icon>
      </div>
    </div>

    <v-btn
      v-if="removable"
      icon
      size="small"
      variant="text"
      class="library-card__remove"
      :aria-label="removeLabel"
      :title="removeLabel"
      @click.stop.prevent="$emit('remove', article.id)"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>
  </v-card>
</template>

<script setup>
defineProps({
  article: {
    type: Object,
    required: true,
  },
  removable: {
    type: Boolean,
    default: false,
  },
  removeLabel: {
    type: String,
    default: "Remove article",
  },
  featured: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["remove"]);

function articleImage(article) {
  return article.urlToImage || article.image_url || article.url || "";
}

function articleSource(article) {
  return (
    article.source || article.source_name || article.name || "Unknown source"
  );
}

function articleDate(article) {
  const raw = article.published || article.publishedAt || article.published_at;
  if (!raw) return "";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function articleExcerpt(article) {
  const text = article.description || article.summary || article.abstract || "";
  if (!text) return "No summary available.";
  return text.length > 220 ? `${text.slice(0, 220)}...` : text;
}

function formatViews(views) {
  if (views === 1) return "1 view";
  if (!views) return "";
  if (views < 1000) return `${views} views`;
  return `${(views / 1000).toFixed(1).replace(".0", "")}k views`;
}
</script>

<style scoped>
.library-card {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  min-height: 190px;
  overflow: hidden;
  border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  border-radius: 8px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.library-card:hover,
.library-card:focus-within {
  border-color: rgba(var(--v-theme-accent), 0.42) !important;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.library-card__image {
  width: 100%;
  height: 100%;
  min-height: 190px;
}

.library-card__body {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 22px 58px 20px 22px;
}

.library-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.library-card__meta span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 8px;
  border: 1px solid rgba(var(--v-theme-accent), 0.22);
  border-radius: 999px;
  background: rgba(var(--v-theme-accent), 0.08);
  color: rgb(var(--v-theme-accent));
  font-size: 0.75rem;
  font-weight: 800;
}

.library-card h2 {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.12rem;
  font-weight: 850;
  line-height: 1.28;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.library-card__excerpt {
  display: -webkit-box;
  margin: 10px 0 16px;
  overflow: hidden;
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-size: 0.94rem;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.library-card__footer {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 0.86rem;
  font-weight: 750;
}

.library-card__footer span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.library-card__remove {
  position: absolute;
  top: 12px;
  right: 12px;
  opacity: 0;
  transition: opacity 0.16s ease;
}

.library-card:hover .library-card__remove,
.library-card:focus-within .library-card__remove {
  opacity: 1;
}

.library-card--featured {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 420px;
  max-height: 520px;
}

.library-card--featured .library-card__image {
  min-height: 420px;
  max-height: 520px;
}

.library-card--featured .library-card__body {
  justify-content: center;
  padding: 40px 48px;
  overflow: hidden;
}

.library-card--featured .library-card__meta span {
  font-size: 0.8rem;
}

.library-card--featured h2 {
  font-size: 1.9rem;
  -webkit-line-clamp: 3;
}

.library-card--featured .library-card__excerpt {
  margin: 16px 0 20px;
  font-size: 1.05rem;
  -webkit-line-clamp: 3;
}

.library-card--compact {
  grid-template-columns: 1fr;
  grid-template-rows: auto minmax(0, 1fr);
  width: 100%;
  min-height: 500px;
}

.library-card--compact .library-card__image {
  aspect-ratio: 16 / 10;
  min-height: 0;
}

.library-card--compact .library-card__body {
  padding: 20px;
}

.library-card--compact h2 {
  -webkit-line-clamp: 3;
}

.library-card--compact .library-card__excerpt {
  -webkit-line-clamp: 3;
}

@media (max-width: 700px) {
  .library-card--featured {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .library-card--featured .library-card__image {
    aspect-ratio: 16 / 9;
    min-height: 0;
  }

  .library-card--featured .library-card__body {
    padding: 22px;
  }

  .library-card--compact {
    min-height: 470px;
  }
}

@media (max-width: 700px) {
  .library-card {
    grid-template-columns: 1fr;
  }

  .library-card__image {
    aspect-ratio: 16 / 9;
    min-height: 0;
  }

  .library-card__body {
    padding: 18px;
  }

  .library-card__remove {
    opacity: 1;
    background: rgb(var(--v-theme-surface));
  }
}
</style>
